import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { OrderService } from '../../services/order.service';
import { NgxSpinnerService } from 'ngx-spinner';
import {
    InvoiceService,
    RefacturaDraft,
    RefacturaPayload,
    RefacturaResult,
} from '../../services/invoice.service';
import { BillingEntityService, BillingEntity } from '../../services/billing-entity.service';
import { REFACTURA_CHECK_ICON, refacturaBodyText, styleRefacturaModal } from '../../shared/refactura-swal';
import { filenameFromResponse, saveBlob } from '../../shared/file-download';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-bill-service',
    templateUrl: './bill-service.component.html',
    styleUrls: ['./bill-service.component.css']
})
export class BillServiceComponent implements OnInit {
    folio: string | null = null;
    serviceId: string | null = null;
    clientName: string | null = null;

    rfc: string | null = null;
    razonSocial: string | null = null;
    regimenFiscalKey: string | null = null;
    regimenFiscalDesc: string | null = null;
    codigoPostal: string | null = null;
    domicilioFiscal: string | null = null;
    email: string | null = null;
    usoCfdiKey: string | null = null;
    usoCfdiDesc: string | null = null;

    billingEntities: BillingEntity[] = [];
    selectedBillingEntityId: number | null = null;
    billingEntityOpen = false;

    paymentForms: { key: string; name: string }[] = [
        { key: '01', name: 'Efectivo' },
        { key: '02', name: 'Cheque' },
        { key: '03', name: 'Transferencia electrónica' },
        { key: '04', name: 'Tarjeta de crédito' },
        { key: '28', name: 'Tarjeta de débito' },
        { key: '99', name: 'Por definir' }
    ];
    selectedPaymentFormKey: string | null = null;
    paymentFormOpen = false;

    paymentMethods: { key: string; name: string }[] = [
        { key: 'PPD', name: 'Pago en parcialidades o diferido' },
        { key: 'PUE', name: 'Pago en una sola exhibición' }
    ];
    selectedPaymentMethodKey: string | null = null;
    paymentMethodOpen = false;

    serviceDescription = '';
    readonly serviceDescriptionMax = 500;

    quantity: number | null = null;
    unitPrice: number | null = null;

    units: { key: string; name: string }[] = [
        { key: 'E48', name: 'Unidad de Servicio' },
        { key: 'HUR', name: 'Hora' },
        { key: 'DAY', name: 'Día' },
        { key: 'MON', name: 'Mes' },
        { key: 'ACT', name: 'Actividad' },
        { key: 'C62', name: 'Uno' }
    ];
    selectedUnitKey: string | null = null;
    unitOpen = false;

    productServiceKeys: { key: string; name: string }[] = [
        { key: '78101800', name: 'Servicios de transporte' },
        { key: '78101500', name: 'Servicios de transporte de carga' },
        { key: '78101600', name: 'Servicios de transporte especializado' },
        { key: '78102000', name: 'Servicios de almacenamiento' },
        { key: '78102100', name: 'Servicios de gestión de inventarios' },
        { key: '78102200', name: 'Servicios de distribución' },
        { key: '78102300', name: 'Servicios logísticos integrales' },
        { key: '01010101', name: 'No existe en el catálogo' }
    ];
    selectedProductServiceKey: string | null = null;
    productServiceOpen = false;

    taxObjectOptions: { key: string; name: string }[] = [
        { key: '01', name: 'No objeto de impuesto' },
        { key: '02', name: 'Sí objeto de impuesto' }
    ];
    selectedTaxObjectKey: string | null = null;
    taxObjectOpen = false;

    uploadedFiles: { file: File; type: 'png' | 'pdf'; previewUrl: string | null }[] = [];

    multiInvoiceOptions: string[] = ['Sí, asociar', 'No asociar'];
    selectedMultiInvoice: string | null = null;
    multiInvoiceOpen = false;

    clientId: number | null = null;
    availableInvoices: any[] = [];
    associatedInvoices: any[] = [];
    invoiceOpen = false;

    paidAmount: number | null = null;

    // --- Modo refacturación (?refacturar=<folio de la factura original>) ---
    // El formulario se reutiliza para corregir un CFDI ya timbrado: llega
    // precargado con los datos del original y genera un sustituto.
    refacturaMode = false;
    originalInvoiceFolio: string | null = null;
    originalInvoiceUuid: string | null = null;
    /** El CFDI original no se pudo leer y los conceptos son una aproximación. */
    refacturaApproximate = false;
    loadingDraft = false;
    private consolidationLoaded = false;

    constructor(
        private route: ActivatedRoute,
        private location: Location,
        private router: Router,
        private orderService: OrderService,
        private invoiceService: InvoiceService,
        private billingEntityService: BillingEntityService,
        private spinner: NgxSpinnerService,
        private elementRef: ElementRef
    ) {}

    ngOnInit(): void {
        const params = this.route.snapshot.queryParamMap;
        this.folio = params.get('folio');
        this.serviceId = params.get('id');
        this.clientName = params.get('client');

        // Se resuelve antes de cargar el servicio: en refacturación el servicio
        // ya tiene factura y loadServiceClient() no debe rebotar a la vista.
        const refacturarFolio = params.get('refacturar');
        if (refacturarFolio) {
            this.refacturaMode = true;
            this.originalInvoiceFolio = refacturarFolio;
        }

        if (this.serviceId) {
            this.loadServiceClient(Number(this.serviceId));
        }

        this.loadBillingEntities();

        if (refacturarFolio) this.loadRefacturaDraft(refacturarFolio);
    }

    // ---------- Refacturación ----------
    private loadRefacturaDraft(folio: string): void {
        // El borrador viaja en el state al venir de la vista de la factura; si se
        // entró por URL directa (o tras un refresh) se vuelve a pedir al backend.
        const cached = (window.history.state ?? {})['refacturaDraft'] as RefacturaDraft | undefined;
        if (cached) {
            this.applyRefacturaDraft(cached);
            return;
        }

        this.loadingDraft = true;
        this.invoiceService.refacturarDraft(folio).subscribe({
            next: (draft) => { this.loadingDraft = false; this.applyRefacturaDraft(draft); },
            error: (err) => {
                this.loadingDraft = false;
                const msg = err?.error?.error ?? 'No se pudo cargar la factura a refacturar.';
                Swal.fire('Error', msg, 'error');
            }
        });
    }

    private applyRefacturaDraft(draft: RefacturaDraft): void {
        this.originalInvoiceFolio = draft.original?.folio ?? this.originalInvoiceFolio;
        this.originalInvoiceUuid = draft.original?.uuid_sat ?? null;
        this.refacturaApproximate = draft.from_xml === false;

        this.selectedBillingEntityId = draft.billing_entity_id ?? this.selectedBillingEntityId;
        this.selectedPaymentFormKey = draft.payment_form_key ?? null;
        this.selectedPaymentMethodKey = draft.payment_method_key ?? null;

        const items = draft.items ?? [];
        const [principal, ...rest] = items;

        // El primer concepto ocupa el paso 3 (concepto a facturar); el resto se
        // trata como servicios consolidados, igual que en el alta normal.
        if (principal) {
            if (principal.service_id != null) {
                this.serviceId = String(principal.service_id);
                this.folio = principal.folio != null ? String(principal.folio) : this.folio;
            }
            this.serviceDescription = principal.service_description ?? '';
            this.quantity = Number(principal.quantity) || null;
            this.selectedUnitKey = principal.unit_key || null;
            this.unitPrice = Number(principal.unit_price) || null;
            this.selectedProductServiceKey = principal.product_service_key || null;
            this.selectedTaxObjectKey = principal.tax_object_key || null;
        }

        const byServiceId = new Map((draft.services ?? []).map(s => [s.id, s]));
        const consolidated = rest
            .filter(i => i.service_id != null)
            .map(i => {
                const svc = byServiceId.get(Number(i.service_id));
                return {
                    id: Number(i.service_id),
                    folio: i.folio ?? svc?.folio ?? null,
                    origin_address: svc?.origin_address ?? null,
                    destination_address: svc?.destination_address ?? null,
                    upload_date: svc?.upload_date ?? null,
                    unit_price: Number(i.unit_price) || 0,
                    // El formulario sólo tiene una descripción (la del concepto
                    // principal); se guarda la propia para no perderla al
                    // reconstruir el CFDI.
                    service_description: i.service_description ?? null,
                };
            });

        if (consolidated.length > 0) {
            this.selectedMultiInvoice = 'Sí, asociar';
            this.availableInvoices = [...consolidated];
            this.associatedInvoices = [...consolidated];
            // No-op si el cliente todavía no carga: en ese caso lo dispara
            // loadServiceClient(). El flag evita que se pida dos veces.
            this.loadBillableForConsolidation();
        }
    }

    private loadBillingEntities(): void {
        this.billingEntityService.list().subscribe({
            next: (rows) => {
                this.billingEntities = rows;
                if (rows.length === 1) this.selectedBillingEntityId = rows[0].id;
            },
            error: () => {
                this.billingEntities = [];
            }
        });
    }

    get selectedBillingEntity(): { id: number; name: string; rfc: string } | null {
        return this.billingEntities.find(e => e.id === this.selectedBillingEntityId) ?? null;
    }

    toggleBillingEntity(): void {
        this.billingEntityOpen = !this.billingEntityOpen;
        if (this.billingEntityOpen) {
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
            this.multiInvoiceOpen = false;
            this.invoiceOpen = false;
        }
    }

    selectBillingEntity(id: number): void {
        this.selectedBillingEntityId = id;
        this.billingEntityOpen = false;
    }

    onAddBillingEntity(): void {
        this.billingEntityOpen = false;
        Swal.fire({
            title: 'Nueva entidad de factura',
            html:
                '<input id="swal-entity-name" class="swal2-input" placeholder="Nombre comercial">' +
                '<input id="swal-entity-legal" class="swal2-input" placeholder="Razón social (exacta como SAT)">' +
                '<input id="swal-entity-rfc" class="swal2-input" placeholder="RFC (12-13 caracteres)" maxlength="13">' +
                '<input id="swal-entity-regimen" class="swal2-input" placeholder="Régimen fiscal (ej. 601)" maxlength="3">' +
                '<input id="swal-entity-cp" class="swal2-input" placeholder="Código postal" maxlength="5">' +
                '<input id="swal-entity-serie" class="swal2-input" placeholder="Serie (opcional, ej. F)" maxlength="10">',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Agregar',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const name = (document.getElementById('swal-entity-name') as HTMLInputElement | null)?.value?.trim();
                const legal = (document.getElementById('swal-entity-legal') as HTMLInputElement | null)?.value?.trim();
                const rfc = (document.getElementById('swal-entity-rfc') as HTMLInputElement | null)?.value?.trim();
                const regimen = (document.getElementById('swal-entity-regimen') as HTMLInputElement | null)?.value?.trim();
                const cp = (document.getElementById('swal-entity-cp') as HTMLInputElement | null)?.value?.trim();
                const serie = (document.getElementById('swal-entity-serie') as HTMLInputElement | null)?.value?.trim();
                if (!name || !legal || !rfc || !regimen || !cp) {
                    Swal.showValidationMessage('Todos los campos excepto serie son obligatorios');
                    return false;
                }
                return { name, legal_name: legal, rfc, regimen_fiscal_key: regimen, postal_code: cp, serie: serie || null };
            }
        }).then(result => {
            if (!result.isConfirmed || !result.value) return;
            this.billingEntityService.create(result.value as Partial<BillingEntity>).subscribe({
                next: (entity) => {
                    this.billingEntities = [...this.billingEntities, entity];
                    this.selectedBillingEntityId = entity.id;
                },
                error: (err) => {
                    Swal.fire('Error', err?.error?.error ?? 'No se pudo guardar la entidad', 'error');
                }
            });
        });
    }

    get selectedPaymentForm(): { key: string; name: string } | null {
        return this.paymentForms.find(f => f.key === this.selectedPaymentFormKey) ?? null;
    }

    togglePaymentForm(): void {
        this.paymentFormOpen = !this.paymentFormOpen;
        if (this.paymentFormOpen) {
            this.billingEntityOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
        }
    }

    selectPaymentForm(key: string): void {
        this.selectedPaymentFormKey = key;
        this.paymentFormOpen = false;
    }

    get selectedPaymentMethod(): { key: string; name: string } | null {
        return this.paymentMethods.find(m => m.key === this.selectedPaymentMethodKey) ?? null;
    }

    togglePaymentMethod(): void {
        this.paymentMethodOpen = !this.paymentMethodOpen;
        if (this.paymentMethodOpen) {
            this.billingEntityOpen = false;
            this.paymentFormOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
        }
    }

    selectPaymentMethod(key: string): void {
        this.selectedPaymentMethodKey = key;
        this.paymentMethodOpen = false;
        // SAT CFDI40105: con PPD la forma de pago debe ser 99 (Por definir).
        if (key === 'PPD') {
            this.selectedPaymentFormKey = '99';
        }
    }

    get selectedUnit(): { key: string; name: string } | null {
        return this.units.find(u => u.key === this.selectedUnitKey) ?? null;
    }

    toggleUnit(): void {
        this.unitOpen = !this.unitOpen;
        if (this.unitOpen) {
            this.billingEntityOpen = false;
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
        }
    }

    selectUnit(key: string): void {
        this.selectedUnitKey = key;
        this.unitOpen = false;
    }

    get selectedProductService(): { key: string; name: string } | null {
        return this.productServiceKeys.find(p => p.key === this.selectedProductServiceKey) ?? null;
    }

    toggleProductService(): void {
        this.productServiceOpen = !this.productServiceOpen;
        if (this.productServiceOpen) {
            this.billingEntityOpen = false;
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.taxObjectOpen = false;
        }
    }

    selectProductService(key: string): void {
        this.selectedProductServiceKey = key;
        this.productServiceOpen = false;
    }

    get selectedTaxObject(): { key: string; name: string } | null {
        return this.taxObjectOptions.find(t => t.key === this.selectedTaxObjectKey) ?? null;
    }

    toggleTaxObject(): void {
        this.taxObjectOpen = !this.taxObjectOpen;
        if (this.taxObjectOpen) {
            this.billingEntityOpen = false;
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
        }
    }

    selectTaxObject(key: string): void {
        this.selectedTaxObjectKey = key;
        this.taxObjectOpen = false;
    }

    onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;
        Array.from(input.files).forEach(file => {
            const isPng = file.type === 'image/png';
            const isPdf = file.type === 'application/pdf';
            if (!isPng && !isPdf) return;
            const entry: { file: File; type: 'png' | 'pdf'; previewUrl: string | null } = {
                file,
                type: isPng ? 'png' : 'pdf',
                previewUrl: null
            };
            if (isPng) {
                const reader = new FileReader();
                reader.onload = () => { entry.previewUrl = reader.result as string; };
                reader.readAsDataURL(file);
            }
            this.uploadedFiles.push(entry);
        });
        input.value = '';
    }

    removeFile(index: number): void {
        this.uploadedFiles.splice(index, 1);
    }

    toggleMultiInvoice(): void {
        this.multiInvoiceOpen = !this.multiInvoiceOpen;
        if (this.multiInvoiceOpen) {
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
        }
    }

    selectMultiInvoice(option: string): void {
        this.selectedMultiInvoice = option;
        this.multiInvoiceOpen = false;
        if (option === 'Sí, asociar') {
            this.loadBillableForConsolidation();
        }
        if (option !== 'Sí, asociar') {
            this.associatedInvoices = [];
        }
    }

    /**
     * Servicios del mismo cliente aún sin facturar, para consolidarlos en un
     * solo CFDI. Se excluye el servicio que ya se factura como concepto
     * principal y se conservan los que ya estén en la lista (en refacturación
     * vienen precargados los del CFDI original, que no salen como pendientes).
     */
    private loadBillableForConsolidation(): void {
        if (!this.clientId || this.consolidationLoaded) return;
        this.consolidationLoaded = true;

        this.invoiceService.billableServices(this.clientId).subscribe({
            next: (res: any) => {
                const list = Array.isArray(res) ? res : [];
                const known = new Set(this.availableInvoices.map(inv => inv.id));
                const pending = list.filter((s: any) => s.id !== Number(this.serviceId) && !known.has(s.id));
                this.availableInvoices = [...this.availableInvoices, ...pending];
            },
            error: () => { this.consolidationLoaded = false; }
        });
    }

    isInvoiceAssociated(id: number): boolean {
        return this.associatedInvoices.some(inv => inv.id === id);
    }

    invoiceSearchText = '';

    get availableInvoicesFiltered(): any[] {
        return this.availableInvoices.filter(inv => !this.isInvoiceAssociated(inv.id));
    }

    get filteredAvailableInvoices(): any[] {
        const term = (this.invoiceSearchText || '').trim().toLowerCase();
        if (!term) return this.availableInvoices;
        return this.availableInvoices.filter(inv => {
            const haystack = [
                inv?.folio,
                inv?.origin_address,
                inv?.destination_address,
                inv?.upload_date
            ].map(v => String(v ?? '').toLowerCase()).join(' ');
            return haystack.includes(term);
        });
    }

    toggleInvoice(): void {
        this.invoiceOpen = !this.invoiceOpen;
        if (this.invoiceOpen) {
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
            this.multiInvoiceOpen = false;
        } else {
            this.invoiceSearchText = '';
        }
    }

    selectInvoice(id: number): void {
        const invoice = this.availableInvoices.find(inv => inv.id === id);
        if (invoice && !this.isInvoiceAssociated(id)) {
            this.associateInvoice(invoice);
        }
    }

    toggleInvoiceSelection(invoice: any): void {
        if (!invoice) return;
        if (this.isInvoiceAssociated(invoice.id)) {
            this.removeAssociatedInvoice(invoice.id);
        } else {
            this.associateInvoice(invoice);
        }
    }

    private associateInvoice(invoice: any): void {
        // En refacturación cada concepto lleva su descripción editable; los
        // servicios que se agregan a mano arrancan con la del concepto principal.
        if (this.refacturaMode && invoice.service_description == null) {
            invoice.service_description = this.serviceDescription.trim();
        }
        this.associatedInvoices.push(invoice);
    }

    removeAssociatedInvoice(id: number): void {
        this.associatedInvoices = this.associatedInvoices.filter(inv => inv.id !== id);
    }

    get currentInvoiceSubtotal(): number {
        const q = Number(this.quantity) || 0;
        const p = Number(this.unitPrice) || 0;
        return q * p;
    }

    get associatedInvoicesTotal(): number {
        return this.associatedInvoices.reduce((sum, s) => sum + (Number(s?.unit_price) || 0), 0);
    }

    /** Cada servicio asociado debe tener un importe > 0 para consolidarse. */
    get associatedServicesValid(): boolean {
        return this.associatedInvoices.every(s => Number(s?.unit_price) > 0);
    }

    get totalSelectedCount(): number {
        return this.associatedInvoices.length + 1;
    }

    get totalSelectedAmount(): number {
        return this.associatedInvoicesTotal + this.currentInvoiceSubtotal;
    }

    get pendingBalance(): number {
        return this.totalSelectedAmount - (Number(this.paidAmount) || 0);
    }

    isGenerating = false;

    get isFormValid(): boolean {
        return !!(
            this.selectedBillingEntityId &&
            this.selectedPaymentFormKey &&
            this.selectedPaymentMethodKey &&
            this.serviceDescription.trim() &&
            this.quantity != null && this.quantity > 0 &&
            this.selectedUnitKey &&
            this.unitPrice != null && this.unitPrice > 0 &&
            this.selectedProductServiceKey &&
            this.selectedTaxObjectKey
        );
    }

    onCancel(): void {
        this.goBack();
    }

    onGenerateInvoice(): void {
        if (!this.isFormValid || !this.serviceId || this.isGenerating) return;

        if (this.refacturaMode) {
            this.saveDraftAndConfirm();
            return;
        }

        const consolidate = this.selectedMultiInvoice === 'Sí, asociar' && this.associatedInvoices.length > 0;
        if (consolidate && !this.associatedServicesValid) {
            Swal.fire('Importe faltante', 'Cada servicio asociado debe tener un importe mayor a 0.', 'warning');
            return;
        }

        this.isGenerating = true;
        const handlers = {
            next: (res: any) => this.handleInvoiceSuccess(res),
            error: (err: any) => this.handleInvoiceError(err),
        };

        if (consolidate) {
            // Un solo CFDI con un concepto por servicio (el principal + los asociados).
            // Clave de producto, unidad, objeto de impuesto y forma/método de pago se
            // comparten; cada servicio aporta su propio importe (cantidad = 1).
            const items = [
                {
                    service_id: Number(this.serviceId),
                    service_description: this.serviceDescription.trim(),
                    quantity: Number(this.quantity),
                    unit_key: this.selectedUnitKey as string,
                    unit_price: Number(this.unitPrice),
                    product_service_key: this.selectedProductServiceKey as string,
                    tax_object_key: this.selectedTaxObjectKey as string,
                },
                ...this.associatedInvoices.map(s => ({
                    service_id: Number(s.id),
                    service_description: this.serviceDescription.trim(),
                    quantity: 1,
                    unit_key: this.selectedUnitKey as string,
                    unit_price: Number(s.unit_price),
                    product_service_key: this.selectedProductServiceKey as string,
                    tax_object_key: this.selectedTaxObjectKey as string,
                })),
            ];
            this.invoiceService.issueConsolidated({
                billing_entity_id: Number(this.selectedBillingEntityId),
                payment_form_key: this.selectedPaymentFormKey as string,
                payment_method_key: this.selectedPaymentMethodKey as string,
                items,
            }).subscribe(handlers);
        } else {
            this.invoiceService.issueCfdi({
                service_id: Number(this.serviceId),
                billing_entity_id: Number(this.selectedBillingEntityId),
                payment_form_key: this.selectedPaymentFormKey as string,
                payment_method_key: this.selectedPaymentMethodKey as string,
                service_description: this.serviceDescription.trim(),
                quantity: Number(this.quantity),
                unit_key: this.selectedUnitKey as string,
                unit_price: Number(this.unitPrice),
                product_service_key: this.selectedProductServiceKey as string,
                tax_object_key: this.selectedTaxObjectKey as string,
            }).subscribe(handlers);
        }
    }

    /**
     * Conceptos capturados en el formulario, en el formato que esperan el
     * guardado del borrador y el timbrado del sustituto.
     */
    private buildRefacturaPayload(): RefacturaPayload {
        const shared = {
            unit_key: this.selectedUnitKey as string,
            product_service_key: this.selectedProductServiceKey as string,
            tax_object_key: this.selectedTaxObjectKey as string,
        };

        return {
            billing_entity_id: Number(this.selectedBillingEntityId),
            payment_form_key: this.selectedPaymentFormKey as string,
            payment_method_key: this.selectedPaymentMethodKey as string,
            items: [
                {
                    service_id: Number(this.serviceId),
                    service_description: this.serviceDescription.trim(),
                    quantity: Number(this.quantity),
                    unit_price: Number(this.unitPrice),
                    ...shared,
                },
                ...this.associatedInvoices.map(s => ({
                    service_id: Number(s.id),
                    service_description: (s.service_description ?? '').trim() || this.serviceDescription.trim(),
                    quantity: 1,
                    unit_price: Number(s.unit_price),
                    ...shared,
                })),
            ],
        };
    }

    /**
     * Guarda la factura corregida y, si todo fue bien, pide confirmación para
     * timbrarla. El borrador queda persistido aunque se cancele aquí.
     */
    private saveDraftAndConfirm(): void {
        if (!this.originalInvoiceFolio) return;

        const payload = this.buildRefacturaPayload();
        if (this.associatedInvoices.length > 0 && !this.associatedServicesValid) {
            Swal.fire('Importe faltante', 'Cada servicio asociado debe tener un importe mayor a 0.', 'warning');
            return;
        }

        this.isGenerating = true;
        this.invoiceService.saveRefacturaDraft(this.originalInvoiceFolio, payload).subscribe({
            next: () => {
                this.isGenerating = false;
                this.confirmStampRefactura(payload);
            },
            error: (err) => {
                this.isGenerating = false;
                const msg = err?.error?.error ?? 'No se pudo guardar la factura modificada.';
                Swal.fire('Error al guardar', msg, 'error');
            }
        });
    }

    /**
     * Confirmación previa al timbrado del CFDI sustituto.
     */
    private confirmStampRefactura(payload: RefacturaPayload): void {
        Swal.fire({
            title: 'Factura modificada guardada con éxito, ¿Timbrar nueva factura?',
            icon: 'info',
            iconHtml: REFACTURA_CHECK_ICON,
            html: refacturaBodyText('Revisamos la información. Si todo es correcto, la nueva factura será timbrada.'),
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: 'Timbrar',
            cancelButtonText: 'Cancelar',
            buttonsStyling: false,
            reverseButtons: true,
            didOpen: () => styleRefacturaModal({ titleLineHeight: '1.35' })
        }).then(result => {
            if (result.isConfirmed) this.stampRefactura(payload);
        });
    }

    /** Timbra el sustituto y cancela la original. */
    private stampRefactura(payload: RefacturaPayload): void {
        if (!this.originalInvoiceFolio) return;

        this.isGenerating = true;
        this.spinner.show('refactura');

        this.invoiceService.refacturar(this.originalInvoiceFolio, payload).subscribe({
            next: (res) => {
                this.isGenerating = false;
                this.spinner.hide('refactura');
                this.showRefacturaResult(res);
            },
            error: (err) => {
                this.isGenerating = false;
                this.spinner.hide('refactura');
                this.handleInvoiceError(err);
            }
        });
    }

    /**
     * Resumen final: folios de la nueva y la original, tipo de relación CFDI
     * y accesos a la factura recién timbrada.
     */
    private showRefacturaResult(res: RefacturaResult): void {
        const newFolio = res.invoice?.folio ?? '';
        const invoiceId = res.invoice?.id ?? null;
        const fileName = res.uuid ?? newFolio;
        const originalFolio = res.original?.folio ?? this.originalInvoiceFolio ?? '';
        const originalState = res.cancellation?.ok === false
            ? 'No se pudo cancelar'
            : (res.original?.status ?? 'Cancelada');

        const btn = 'height:40px;border-radius:0.5rem;font-family:Lato,sans-serif;'
            + 'font-weight:600;font-size:14px;cursor:pointer;';
        const primaryBtn = `${btn}width:100%;background:#00A0E1;color:#FFFFFF;border:1px solid #00A0E1;`;
        const outlineBtn = `${btn}flex:1;background:#FFFFFF;color:#00A0E1;border:1px solid #00A0E1;`;

        const cancelWarning = res.cancellation?.ok === false
            ? `<div style="margin-top:14px;background:#FFF6E5;border:1px solid #FFD98A;border-radius:8px;
                   padding:10px 12px;color:#8A6100;font-family:Lato,sans-serif;font-size:13px;text-align:left">
                   La nueva factura se timbró, pero la original no pudo cancelarse
                   (${res.cancellation?.error ?? 'error desconocido'}). Cancélala manualmente
                   con motivo 01 y UUID ${res.uuid ?? ''}.
               </div>`
            : '';

        Swal.fire({
            icon: 'info',
            iconHtml: REFACTURA_CHECK_ICON,
            html: `
                <div style="background:#E9F7EC;border-radius:8px;padding:10px 12px;margin:6px 0 18px">
                    <span style="color:#3B9E4E;font-family:Poppins,sans-serif;font-weight:700;
                                 font-size:13px;letter-spacing:0.08em">REFACTURACIÓN COMPLETADA</span>
                </div>
                <div style="display:flex;flex-direction:column;gap:12px;text-align:left;
                            font-family:Lato,sans-serif;font-size:16px">
                    ${this.resultRow('Nueva factura:', `Folio ${newFolio}`, 'Timbrada')}
                    ${this.resultRow('Factura original:', `Folio ${originalFolio}`, originalState)}
                    ${this.resultRow('Relación CFDI:', res.relation?.description ?? 'Sustitución de los CFDI previos')}
                </div>
                ${cancelWarning}
                <div style="display:flex;flex-direction:column;gap:10px;margin-top:22px">
                    <button id="rf-view" style="${primaryBtn}">Ver nueva factura</button>
                    <div style="display:flex;gap:10px">
                        <button id="rf-xml" style="${outlineBtn}">Descargar XML</button>
                        <button id="rf-pdf" style="${outlineBtn}">Descargar PDF</button>
                    </div>
                </div>`,
            showConfirmButton: false,
            allowOutsideClick: false,
            didOpen: () => {
                styleRefacturaModal();
                document.getElementById('rf-view')?.addEventListener('click', () => {
                    Swal.close();
                    if (newFolio) this.router.navigate(['/service/list/folio-' + newFolio + '/view']);
                });
                document.getElementById('rf-xml')?.addEventListener('click',
                    () => this.downloadInvoiceFile(invoiceId, 'xml', fileName));
                document.getElementById('rf-pdf')?.addEventListener('click',
                    () => this.downloadInvoiceFile(invoiceId, 'pdf', fileName));
            }
        });
    }

    /** Fila etiqueta / valor del resumen, con estado opcional entre paréntesis. */
    private resultRow(label: string, value: string, state?: string): string {
        const suffix = state ? ` <span style="color:#8A8A8A;font-weight:400">(${state})</span>` : '';
        return `
            <div style="display:flex;justify-content:space-between;gap:16px;align-items:baseline">
                <span style="color:#545454">${label}</span>
                <span style="color:#222222;text-align:right"><b>${value}</b>${suffix}</span>
            </div>`;
    }

    private downloadInvoiceFile(invoiceId: number | null, kind: 'xml' | 'pdf', name: string): void {
        if (!invoiceId) return;

        const request = kind === 'xml'
            ? this.invoiceService.downloadXml(invoiceId)
            : this.invoiceService.downloadPdf(invoiceId);

        request.subscribe({
            next: (res) => saveBlob(res.body, filenameFromResponse(res, `${name}.${kind}`)),
            error: () => Swal.fire(kind.toUpperCase(), `No se pudo descargar el ${kind.toUpperCase()}.`, 'error'),
        });
    }

    private handleInvoiceSuccess(res: any): void {
        this.isGenerating = false;
        Swal.fire({
            icon: 'success',
            title: 'CFDI timbrado',
            html: `UUID: <b>${res.uuid}</b><br>Folio: <b>${res.invoice?.folio ?? ''}</b>`,
            confirmButtonText: 'Ver factura'
        }).then(() => {
            const folio = res.invoice?.folio;
            if (folio) {
                this.router.navigate(['/service/list/folio-' + folio + '/view']);
            } else {
                this.router.navigate(['/service/list']);
            }
        });
    }

    private handleInvoiceError(err: any): void {
        this.isGenerating = false;
        const ve = err?.error?.validation_errors;
        if (Array.isArray(ve) && ve.length) {
            const items = ve.map((e: any) => `<li><b>${e.code}</b> — ${e.message}</li>`).join('');
            Swal.fire({
                title: 'Datos inválidos (no se gastó timbre)',
                html: `<ul style="text-align:left;margin:0;padding-left:18px">${items}</ul>`,
                icon: 'warning',
            });
            return;
        }
        const msg = err?.error?.error ?? 'No se pudo generar la factura.';
        Swal.fire('Error al timbrar', msg, 'error');
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.billingEntityOpen = false;
            this.paymentFormOpen = false;
            this.paymentMethodOpen = false;
            this.unitOpen = false;
            this.productServiceOpen = false;
            this.taxObjectOpen = false;
            this.multiInvoiceOpen = false;
            this.invoiceOpen = false;
        }
    }

    private loadServiceClient(id: number): void {
        this.orderService.getId(id).subscribe((res: any) => {
            // En refacturación el servicio ya está facturado: ese es justamente
            // el caso que se está corrigiendo, así que no se rebota a la vista.
            if (res?.invoice_folio && !this.refacturaMode) {
                this.router.navigate(['/service/list/folio-' + res.invoice_folio + '/view']);
                return;
            }
            const client = res?.client;
            if (!client) return;
            this.clientId = client.id ?? null;
            this.clientName = client.name ?? this.clientName;
            this.rfc = client.rfc ?? null;
            this.razonSocial = client.business_name ?? null;
            this.regimenFiscalKey = client.fiscal_regime?.key ?? null;
            this.regimenFiscalDesc = client.fiscal_regime?.description ?? null;
            this.codigoPostal = client.postal_code ?? null;
            this.domicilioFiscal = client.fiscal_address ?? null;
            this.email = client.email ?? null;
            this.usoCfdiKey = client.cfdi_use?.key ?? null;
            this.usoCfdiDesc = client.cfdi_use?.description ?? null;

            // En refacturación el panel de consolidación ya viene abierto con los
            // servicios del CFDI original; se completa con los pendientes ahora
            // que se conoce el cliente.
            if (this.refacturaMode && this.selectedMultiInvoice === 'Sí, asociar') {
                this.loadBillableForConsolidation();
            }
        });
    }

    goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.router.navigate(['/service/list']);
        }
    }

    onPreview(): void {
        // Placeholder: acción de vista previa pendiente de implementar.
    }
}
